import type { HeadingLevel } from '@sparklayer-io/spark-dash-ui/components/ui/heading/types';
import { cn } from '@sparklayer-io/spark-dash-ui/lib/styles';
import { Heading, LearnMoreLink, Text } from '@ui/themed';
import type { PropsWithChildren, ReactNode } from 'react';

/**
 * A titled section of a page: a small heading, an optional description and/or action beside it,
 * then the section's content.
 *
 * `headingLevel` defaults to `h2` because `Page` owns the `h1` — a section sitting directly in a
 * page is one level down. Pass `h3` where an `h2` already owns the surrounding region (a
 * `Card.Header`, say), so the outline doesn't repeat a level.
 */
export function PageSection({
  title,
  headingLevel = 'h2',
  description,
  learnMoreUrl,
  action,
  children,
}: PropsWithChildren<{
  title: string;
  /** Defaults to `h2`. Drop to `h3` where an `h2` already owns the surrounding region. */
  headingLevel?: HeadingLevel;
  description?: string;
  learnMoreUrl?: string;
  /** Rendered at the end of the header row — a `Button`, `ExternalLink` or similar. */
  action?: ReactNode;
}>) {
  const hasSubtitle = !!description || !!learnMoreUrl;

  return (
    <section className="flex flex-col gap-3">
      <div
        className={cn(
          'flex justify-between gap-3',
          // A description makes the heading block taller, so the action aligns to the title
          // rather than to the middle of the whole block.
          hasSubtitle ? 'items-start' : 'items-center',
        )}
      >
        <div className="flex min-w-0 flex-col">
          <Heading style="sm" level={headingLevel}>
            {title}
          </Heading>
          {hasSubtitle && (
            <Text size="sm" variant="secondary">
              {description}
              {learnMoreUrl && (
                <>
                  {' '}
                  <LearnMoreLink link={learnMoreUrl} />
                </>
              )}
            </Text>
          )}
        </div>
        {action}
      </div>
      <div className="flex flex-col gap-4">{children}</div>
    </section>
  );
}
